/**
 * Utility functions for generating campaign names
 */

/**
 * Today's date in YYYY-MM-DD for local timezone (HTML date input value).
 */
export const getLocalDateInputValue = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Default empty campaign form with today's campaign date pre-filled.
 */
export const getEmptyCampaignForm = () => ({
  campaignName: '',
  source: '',
  sourceRemarks: '',
  pricePerMessage: '',
  campaignDate: getLocalDateInputValue(),
  objective: '',
  offers: [],
  otherOfferDetails: '',
  coupon: ''
});

/**
 * Get short form for campaign source
 * @param {string} source - The source value (e.g., 'email', 'whatsapp', 'SMS', 'calling', 'others')
 * @returns {string} Short form code
 */
export const getSourceShortForm = (source) => {
  const sourceMap = {
    'email': 'EM',
    'whatsapp': 'WA',
    'SMS': 'SMS',
    'calling': 'CAL',
    'others': 'OHR'
  };
  
  return sourceMap[source?.toLowerCase()] || '';
};

/**
 * Get short form for campaign objective
 * @param {string} objective - The objective value (e.g., 'Abandoned', 'Cart', 'Retention', etc.)
 * @returns {string} Short form code
 */
export const getObjectiveShortForm = (objective) => {
  const objectiveMap = {
    'Abandoned': 'ABAN',
    'Acquisition': 'ACQ',
    'Retention': 'RET',
    'Issue orders': 'IO',
    'Dead User': 'DU',
    'Others': 'OHR',
    'Cart': 'Cart' // legacy records only; no longer offered in UI
  };
  
  return objectiveMap[objective] || '';
};

/**
 * Format date to YYYYMMDD format
 * @param {string} dateString - Date string (YYYY-MM-DD format)
 * @returns {string} Formatted date string (YYYYMMDD)
 */
const formatDateToYYYYMMDD = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}${month}${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Generate campaign name based on format: YYYYMMDD_{source}_{objective}_{coupon}
 * @param {Object} campaignData - Campaign data object
 * @param {string} campaignData.campaignDate - Date of campaign (YYYY-MM-DD format)
 * @param {string} campaignData.source - Source of campaign
 * @param {string} campaignData.objective - Objective of campaign
 * @param {string} campaignData.coupon - Coupon code (optional)
 * @returns {string} Generated campaign name
 */
export const generateCampaignName = (campaignData) => {
  const { campaignDate, source, objective, coupon } = campaignData || {};
  
  // Format date
  const datePart = formatDateToYYYYMMDD(campaignDate);
  
  // Get short forms
  const sourcePart = getSourceShortForm(source);
  const objectivePart = getObjectiveShortForm(objective);
  
  // Build the campaign name parts
  const parts = [];
  
  if (datePart) {
    parts.push(datePart);
  }
  
  if (sourcePart) {
    parts.push(sourcePart);
  }
  
  if (objectivePart) {
    parts.push(objectivePart);
  }
  
  if (coupon && coupon.trim()) {
    parts.push(coupon.trim().toUpperCase());
  }
  
  // Join parts with underscore
  return parts.join('_');
};

